import { api } from '@/lib/api/client';
import type {
  ActivityType,
  TaskType,
  PaymentMethod,
  ExpenseCategory,
  MatterPracticeArea,
  ContractPracticeArea,
  ContractPeriod,
  QaCriterion,
  SettingsFilters,
  PaginatedResponse,
  CreateNameDto,
  UpdateNameDto,
  CreateContractPeriodDto,
  UpdateContractPeriodDto,
  CreateQaCriterionDto,
  UpdateQaCriterionDto,
} from '../types';

// Helper to build query params
function buildParams(filters?: SettingsFilters): Record<string, unknown> {
  if (!filters) return {};

  const params: Record<string, unknown> = {};

  if (filters.page !== undefined && filters.limit) {
    params.offset = filters.page * filters.limit;
    params.limit = filters.limit;
  }

  if (filters.orderBy && filters.order) {
    params.orderBy = filters.orderBy;
    params.order = filters.order;
  }

  if (filters.s && filters.s.length >= 2) {
    params.s = filters.s;
  }

  if (filters['createdAt.min']) {
    const minDate = new Date(filters['createdAt.min']);
    minDate.setHours(0, 0, 0, 0);
    params['createdAt.min'] = minDate.toISOString();
  }

  if (filters['createdAt.max']) {
    const maxDate = new Date(filters['createdAt.max']);
    maxDate.setHours(23, 59, 59, 999);
    params['createdAt.max'] = maxDate.toISOString();
  }

  if (filters.is_active !== undefined) {
    params.is_active = filters.is_active;
  }

  if (filters.type) {
    params.type = filters.type;
  }

  return params;
}

// Activity Types API
export const activityTypesApi = {
  getAll: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<ActivityType>>('/Activity-Types', { params: buildParams(filters) }),
  getById: (id: number | string) =>
    api.get<ActivityType>(`/Activity-Types/${id}`),
  create: (data: CreateNameDto) =>
    api.post<ActivityType>('/Activity-Types', data),
  update: (id: number | string, data: UpdateNameDto) =>
    api.patch<ActivityType>(`/Activity-Types/${id}`, data),
  delete: (id: number | string) =>
    api.delete(`/Activity-Types/${id}`),
};

// Task Types API
export const taskTypesApi = {
  getAll: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<TaskType>>('/Task-Types', { params: buildParams(filters) }),
  getById: (id: number | string) =>
    api.get<TaskType>(`/Task-Types/${id}`),
  create: (data: CreateNameDto) =>
    api.post<TaskType>('/Task-Types', data),
  update: (id: number | string, data: UpdateNameDto) =>
    api.patch<TaskType>(`/Task-Types/${id}`, data),
  delete: (id: number | string) =>
    api.delete(`/Task-Types/${id}`),
};

// Payment Methods API
export const paymentMethodsApi = {
  getAll: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<PaymentMethod>>('/Payment-Methods', { params: buildParams(filters) }),
  getById: (id: number | string) =>
    api.get<PaymentMethod>(`/Payment-Methods/${id}`),
  create: (data: CreateNameDto) =>
    api.post<PaymentMethod>('/Payment-Methods', data),
  update: (id: number | string, data: UpdateNameDto) =>
    api.patch<PaymentMethod>(`/Payment-Methods/${id}`, data),
  delete: (id: number | string) =>
    api.delete(`/Payment-Methods/${id}`),
};

// Expense Categories API
export const expenseCategoriesApi = {
  getAll: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<ExpenseCategory>>('/expense-category', { params: buildParams(filters) }),
  getById: (id: number | string) =>
    api.get<ExpenseCategory>(`/expense-category/${id}`),
  create: (data: CreateNameDto) =>
    api.post<ExpenseCategory>('/expense-category', data),
  update: (id: number | string, data: UpdateNameDto) =>
    api.patch<ExpenseCategory>(`/expense-category/${id}`, data),
  delete: (id: number | string) =>
    api.delete(`/expense-category/${id}`),
};

// Matter Practice Areas API
export const matterPracticeAreasApi = {
  getAll: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<MatterPracticeArea>>('/Matter-Practice-Areas', { params: buildParams(filters) }),
  getById: (id: number | string) =>
    api.get<MatterPracticeArea>(`/Matter-Practice-Areas/${id}`),
  create: (data: CreateNameDto) =>
    api.post<MatterPracticeArea>('/Matter-Practice-Areas', data),
  update: (id: number | string, data: UpdateNameDto) =>
    api.patch<MatterPracticeArea>(`/Matter-Practice-Areas/${id}`, data),
  delete: (id: number | string) =>
    api.delete(`/Matter-Practice-Areas/${id}`),
};

// Contract Practice Areas API
export const contractPracticeAreasApi = {
  getAll: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<ContractPracticeArea>>('/Contract-Practice-Areas', { params: buildParams(filters) }),
  getById: (id: number | string) =>
    api.get<ContractPracticeArea>(`/Contract-Practice-Areas/${id}`),
  create: (data: CreateNameDto) =>
    api.post<ContractPracticeArea>('/Contract-Practice-Areas', data),
  update: (id: number | string, data: UpdateNameDto) =>
    api.patch<ContractPracticeArea>(`/Contract-Practice-Areas/${id}`, data),
  delete: (id: number | string) =>
    api.delete(`/Contract-Practice-Areas/${id}`),
};

// Contract Periods API
export const contractPeriodsApi = {
  getAll: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<ContractPeriod>>('/Contract-Periods', { params: buildParams(filters) }),
  getById: (id: number | string) =>
    api.get<ContractPeriod>(`/Contract-Periods/${id}`),
  create: (data: CreateContractPeriodDto) =>
    api.post<ContractPeriod>('/Contract-Periods', data),
  update: (id: number | string, data: UpdateContractPeriodDto) =>
    api.patch<ContractPeriod>(`/Contract-Periods/${id}`, data),
  delete: (id: number | string) =>
    api.delete(`/Contract-Periods/${id}`),
};

// QA Criteria API
export const qaCriteriaApi = {
  getAll: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<QaCriterion>>('/qa-criteria', { params: buildParams(filters) }),
  getSelectBox: (filters?: SettingsFilters) =>
    api.get<PaginatedResponse<QaCriterion>>('/qa-criteria/select-box', { params: buildParams(filters) }),
  getById: (id: number | string) =>
    api.get<QaCriterion>(`/qa-criteria/${id}`),
  create: (data: CreateQaCriterionDto) =>
    api.post<QaCriterion>('/qa-criteria', data),
  update: (id: number | string, data: UpdateQaCriterionDto) =>
    api.patch<QaCriterion>(`/qa-criteria/${id}`, data),
  delete: (id: number | string) =>
    api.delete(`/qa-criteria/${id}`),
};

// Query keys
export const settingsQueryKeys = {
  activityTypes: {
    all: ['activityTypes'] as const,
    lists: () => [...settingsQueryKeys.activityTypes.all, 'list'] as const,
    list: (filters?: SettingsFilters) => [...settingsQueryKeys.activityTypes.lists(), filters] as const,
    detail: (id: number | string) => [...settingsQueryKeys.activityTypes.all, 'detail', id] as const,
  },
  taskTypes: {
    all: ['taskTypes'] as const,
    lists: () => [...settingsQueryKeys.taskTypes.all, 'list'] as const,
    list: (filters?: SettingsFilters) => [...settingsQueryKeys.taskTypes.lists(), filters] as const,
    detail: (id: number | string) => [...settingsQueryKeys.taskTypes.all, 'detail', id] as const,
  },
paymentMethods: {
    all: ['paymentMethods'] as const,
    lists: () => [...settingsQueryKeys.paymentMethods.all, 'list'] as const,
    list: (filters?: SettingsFilters) => [...settingsQueryKeys.paymentMethods.lists(), filters] as const,
    detail: (id: number | string) => [...settingsQueryKeys.paymentMethods.all, 'detail', id] as const,
  },
  expenseCategories: {
    all: ['expenseCategories'] as const,
    lists: () => [...settingsQueryKeys.expenseCategories.all, 'list'] as const,
    list: (filters?: SettingsFilters) => [...settingsQueryKeys.expenseCategories.lists(), filters] as const,
    detail: (id: number | string) => [...settingsQueryKeys.expenseCategories.all, 'detail', id] as const,
  },
  matterPracticeAreas: {
    all: ['matterPracticeAreas'] as const,
    lists: () => [...settingsQueryKeys.matterPracticeAreas.all, 'list'] as const,
    list: (filters?: SettingsFilters) => [...settingsQueryKeys.matterPracticeAreas.lists(), filters] as const,
    detail: (id: number | string) => [...settingsQueryKeys.matterPracticeAreas.all, 'detail', id] as const,
  },
  contractPracticeAreas: {
    all: ['contractPracticeAreas'] as const,
    lists: () => [...settingsQueryKeys.contractPracticeAreas.all, 'list'] as const,
    list: (filters?: SettingsFilters) => [...settingsQueryKeys.contractPracticeAreas.lists(), filters] as const,
    detail: (id: number | string) => [...settingsQueryKeys.contractPracticeAreas.all, 'detail', id] as const,
  },
  contractPeriods: {
    all: ['contractPeriods'] as const,
    lists: () => [...settingsQueryKeys.contractPeriods.all, 'list'] as const,
    list: (filters?: SettingsFilters) => [...settingsQueryKeys.contractPeriods.lists(), filters] as const,
    detail: (id: number | string) => [...settingsQueryKeys.contractPeriods.all, 'detail', id] as const,
  },
  qaCriteria: {
    all: ['qaCriteria'] as const,
    lists: () => [...settingsQueryKeys.qaCriteria.all, 'list'] as const,
    list: (filters?: SettingsFilters) => [...settingsQueryKeys.qaCriteria.lists(), filters] as const,
    detail: (id: number | string) => [...settingsQueryKeys.qaCriteria.all, 'detail', id] as const,
  },
};
